import { useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { Check, ChevronDown, Store } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLotacao, useUnidades, verUnidade } from "./dados";

/**
 * Unidade no topo da tela (migration 308): o administrador vê a organização
 * inteira ou uma unidade. O super admin escolhe a organização no "Ver como"
 * e, depois, a unidade (310). A escolha mora no banco (unidade_visao) e vale
 * para todas as consultas, igual ao "Ver como"; por isso troca e recarrega.
 * Com uma unidade ativa só, nada aparece.
 */
export const SeletorUnidade = () => {
  const { data: identity } = useGetIdentity();
  const administrador =
    identity?.gestao_unidade === "administrador" || (!!identity?.is_super_admin && !identity?.perfil_leitura);
  const { data: lotacao } = useLotacao(administrador);
  const { data: unidades } = useUnidades(administrador);
  const notify = useNotify();
  const [trocando, setTrocando] = useState(false);

  if (!administrador || !lotacao || lotacao.unidades_ativas < 2) return null;
  const ativas = (unidades ?? []).filter((u) => u.ativa || u.id === lotacao.filtro);

  const trocar = async (id: string | null) => {
    if (id === lotacao.filtro) return;
    setTrocando(true);
    try {
      await verUnidade(id);
      window.location.reload();
    } catch {
      setTrocando(false);
      notify("Não foi possível trocar de unidade.", { type: "error" });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={trocando}
          className="inline-flex h-8 max-w-[40vw] cursor-pointer items-center gap-2 rounded-lg border border-white/25 px-2.5 text-sm font-medium hover:bg-white/10 disabled:opacity-60 dark:border-border dark:hover:bg-muted"
          aria-label="Unidade"
        >
          <Store className="h-4 w-4 shrink-0" />
          <span className="hidden truncate sm:inline">{lotacao.filtro_nome ?? "Todas as unidades"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-white/70 dark:text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Unidade</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="flex cursor-pointer items-center gap-2" onSelect={() => trocar(null)}>
          <span className="flex-1 truncate">Todas as unidades</span>
          {!lotacao.filtro && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        {ativas.map((u) => (
          <DropdownMenuItem key={u.id} className="flex cursor-pointer items-center gap-2" onSelect={() => trocar(u.id)}>
            <span className="flex-1 truncate">{u.nome}</span>
            {u.id === lotacao.filtro && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
